import { CodeStatus, MethodOfWork, PermitCategory, PermitType } from "../../common/enums";
import Person from "../Person";
import Position from "../Position";
import Signature from "../Signature";
import { permitContentVersions } from "../WorkPermit";
import Measures, { Measures_normalize } from "./conditions/Measures";
import ProtectiveEquipment from "./conditions/ProtectiveEquipment";
import { RiskIdentifications, RiskIdentifications_normalize } from "./conditions/RiskIdentifications";

type GeneralDepartmentPermit = {
    id: string;
    departmentId: number;
    entityId: number;
    status: CodeStatus;
    positions: Position[];
    startDate: Date;
    endDate: Date;
    remindMe: boolean;
    expirationReminderEmail: string;
    expirationReminderPhoneNumber: string;
    remindMeDate: Date;
    permitCategory: PermitCategory;
    entrepreneur: string;
    description: string;
    workLeaderName: string;
    workLeaderIsActive: boolean;
    workLeaderPhoneNumber: string;
    persons: Person[];
    projectLeaderName: string;
    projectLeaderPhoneNumber: string;
    projectLeaderIsActive: boolean;
    isWorkFlammable: boolean;
    methodOfWork: MethodOfWork[];
    otherMethodOfWork: string;
    isAuthorizedForHotWork: boolean;
    surveillanceByFireWatch: boolean;
    fireWatchName: string;
    fireWatchNotNeeded: boolean;
    postWorkSurveillanceArranged: boolean;
    postWorkSurveillanceFireWatch: string;
    postWorkSurveillanceHours: number;
    forWorkWithFlammableGoods: boolean;
    workplaceCleaned: boolean;
    combustibleMaterialsRemoved: boolean;
    inAreaWithExplosiveAtmosphere: boolean;
    openings: boolean;
    extinguishingEquipment: boolean;
    weldingEquipment: boolean;
    weldingEquipmentFaultless: boolean;
    emergencyServicesCanBeCalled: boolean;
    waterproofing: boolean;
    openFlamesForMeltingIce: boolean;
    maximumHeat: boolean;
    whenMeltingAsphalt: boolean;
    followingSections: string;
    permitAgreementStartDate: Date;
    permitAgreementEndDate: Date;
    permitAgreementFireWatch: string;
    permitAgreementWayOfCommunication: string;
    permitAgreementProviderSignature: Signature;
    permitAgreementSeekerSignature: Signature;
    isFinishedPermitProviderSignature: Signature;
    isFinishedPermitSeekerSignature: Signature;
    createdByUserName?: string;
    sequenceIdentifier: string;
    version: number;
    renewalOfPermitSequenceIdentifier: string;
    initialStartDate: Date;
    renewedByPermitSequenceIdentifier: string;
    extendedEndDate: Date;
    lastInSeriesDocumentId: string;
    isFinished: boolean;

    measures: Measures;
    protectiveEquipment: ProtectiveEquipment;
    riskIdentifications: RiskIdentifications;

    contentVersion: number;

    canRenew: boolean;
    canCheckout: boolean;
    canUpdate: boolean;
    canManageAttachments: boolean;
    canAccept: boolean;
    canUpdatePermitAgreementEndDate: boolean;
    canUpdatePositionActiveStatus: boolean;
    canUpdateParticipants: boolean;
    canViewFinishPermitLink: boolean;
    canManagePositionPolygons: boolean;

    permitName: PermitType.GeneralDepartmentPermit;
};

export default GeneralDepartmentPermit;

export function GeneralDepartmentPermit_normalize(permit: GeneralDepartmentPermit): GeneralDepartmentPermit {
    const processedPermit: GeneralDepartmentPermit = {
        ...permit,
        startDate: new Date(permit.startDate),
        endDate: new Date(permit.endDate),
        permitAgreementStartDate: new Date(permit.permitAgreementStartDate),
        permitAgreementEndDate: new Date(permit.permitAgreementEndDate),
        remindMeDate: permit.remindMeDate ? new Date(permit.remindMeDate) : new Date(),
        permitAgreementProviderSignature: {
            ...permit.permitAgreementProviderSignature,
            createdAt: new Date(permit.permitAgreementProviderSignature.createdAt)
        },
        permitAgreementSeekerSignature: {
            ...permit.permitAgreementSeekerSignature,
            createdAt: new Date(permit.permitAgreementSeekerSignature.createdAt)
        },
        isFinishedPermitProviderSignature: {
            ...permit.isFinishedPermitProviderSignature,
            createdAt: new Date(permit.isFinishedPermitProviderSignature.createdAt)
        },
        isFinishedPermitSeekerSignature: {
            ...permit.isFinishedPermitSeekerSignature,
            createdAt: new Date(permit.isFinishedPermitSeekerSignature.createdAt)
        },
        initialStartDate: new Date(permit.initialStartDate),
        extendedEndDate: new Date(permit.extendedEndDate),
        measures: Measures_normalize(permit.measures),
        riskIdentifications: RiskIdentifications_normalize(permit.riskIdentifications),
        contentVersion: permit.contentVersion ?? Math.max(...Object.values(permitContentVersions)),
        canManagePositionPolygons: !!permit.canManagePositionPolygons
    };

    return processedPermit;
}
